// Version Cohorts: did the last release move the numbers?
//
// Pick a deploy from the dropdown; every metric is then shown for the window
// BEFORE it started serving and the equal-length window since, side by side, with
// the change between them. One aggregate payload
// (`GET /api/admin/version-cohorts`), so the cards, the deltas and the trend
// lines can never disagree.
//
// Three things about the design are deliberate:
//
// - **The trend line is not decoration.** Two summary figures cannot tell a step
//   change at the deploy apart from a drift that started the week before, and that
//   distinction is the entire question this page exists to answer. Every card
//   draws its daily series with the boundary marked, so a reader can see which one
//   they are looking at.
// - **Every number says how much evidence it rests on.** The observation count
//   sits under each side and a thin cohort is called out in words. A release that
//   shipped this morning has a real "after" figure resting on nine data points,
//   and a page that presented it like a fortnight of data would be lying by
//   omission.
// - **An unmeasured side reads "-", never 0.** Null is unknown. Rendering it as
//   zero turns "no traffic yet" into "the metric collapsed", which is the most
//   expensive misread available here.
//
// Every control lives in the URL (`?release=`, `?window=`, `?section=`, and the
// two cohort filters `?devices=` / `?countries=`), so a comparison is a link you
// can send someone rather than a view you have to talk them through rebuilding.
//
// A DEVICE or COUNTRY selection moves the page onto the on-demand SLICE lane
// (`GET /api/admin/version-cohorts/slice`): the whole-population series is
// materialized by the worker every few minutes, but a cohort nobody could
// precompute is built when asked for, exactly like the Funnel's custom range, and
// the page polls until it is ready. A metric that cannot be narrowed to a cohort
// (SCOUT's cost per search has no project to narrow by) says so on its card while
// a filter is active, rather than showing a platform-wide number under a cohort
// heading.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { TableExport } from "@recruiter/shared";
import type { CSSProperties } from "react";
import type { CohortMetric, CohortSide, CohortWindow } from "@recruiter/shared/types";

import { AreaBoundary } from "../components/area-gate";
import { CohortTabs } from "../components/cohort-tabs";
import { CountryFilter } from "../components/country-filter";
import { DeviceFilter } from "../components/device-filter";
import { EmptyState, LoadingState } from "../components/states";
import { formatClock, formatDateTime } from "../lib/format";
import {
  adminKeys,
  funnelRangeGaveUp,
  versionCohortsQuery,
  versionCohortsSliceQuery,
} from "../lib/queries";
import {
  BREAKDOWN_OTHER,
  boundaryDayOf,
  boundaryX,
  breakdownBoundaryX,
  breakdownDays,
  breakdownLines,
  breakdownPolylines,
  COHORT_SECTION_LABELS,
  COHORT_SECTIONS,
  COHORT_WINDOW_LABELS,
  COHORT_WINDOWS,
  type BreakdownLine,
  type CohortSection,
  deployLabel,
  evidenceNote,
  formatDeltaPct,
  formatMetric,
  formatShare,
  formatShareDelta,
  sectionOf,
  shortRelease,
  sparkPoints,
  splitParts,
} from "../lib/version-cohorts";
import "./version-cohorts.css";

/** Viewbox of a card's trend line. A fixed coordinate space the unit-box points
 *  from `sparkPoints` scale into; the SVG itself is responsive via CSS. */
const SPARK_W = 320;
const SPARK_H = 56;
/** Breathing room top and bottom so a peak or trough is not clipped by the edge. */
const SPARK_PAD = 4;

/** How many distinct series colours the console's categorical ramp holds
 *  (`--rc-chart-1..8`, globals.css). A breakdown shows at most `BREAKDOWN_HEAD`
 *  lines plus the folded tail, which wears the muted text colour instead. */
const SERIES_COLOURS = 8;

function SparkLine({ metric, boundaryDay }: { metric: CohortMetric; boundaryDay: string | null }) {
  const points = sparkPoints(metric);
  if (points.length < 2) return null;
  const inner = SPARK_H - SPARK_PAD * 2;
  const path = points
    .map(
      (p, i) =>
        `${i === 0 ? "M" : "L"}${(p.x * SPARK_W).toFixed(1)} ${(SPARK_PAD + p.y * inner).toFixed(1)}`,
    )
    .join(" ");
  const marker = boundaryX(metric, boundaryDay);
  return (
    <svg
      className="vc-spark"
      viewBox={`0 0 ${SPARK_W} ${SPARK_H}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={`${metric.label}: daily trend over the last 90 days`}
    >
      {marker === null ? null : (
        <line
          className="vc-spark__boundary"
          x1={marker * SPARK_W}
          x2={marker * SPARK_W}
          y1={0}
          y2={SPARK_H}
        />
      )}
      <path className="vc-spark__line" d={path} />
    </svg>
  );
}

/** The counts behind a two-outcome rate: one stacked bar per side, both lanes
 *  named and tallied.
 *
 *  This exists because a SHARE cannot answer "how many chose what", and every
 *  other number on this page is a share. "Half took the search" is the same
 *  sentence over four answers and over four hundred, and the two call for
 *  opposite decisions.
 *
 *  The bar is stacked rather than paired because the two lanes are the SAME
 *  population split in two: they sum to every answer given, so one full-width bar
 *  per side puts the shaded fraction at exactly the percentage printed above it
 *  and the two sides on one length scale. The lanes keep their order and their
 *  colour in both bars, in the legend, and in the tally, so identity never rests
 *  on colour alone - and the segments are separated by a surface-coloured gap so
 *  the boundary survives a colourblind reader, a print-out and forced colours.
 *
 *  A side that observed nothing says so in words instead of drawing two empty
 *  lanes: no answers yet is not "nobody picked either", and the same
 *  unknown-is-never-zero rule governs the figure above it. */
function SplitChart({ metric }: { metric: CohortMetric }) {
  const split = metric.split;
  if (!split) return null;
  const sides: { caption: string; side: CohortSide }[] = [
    { caption: "Before", side: metric.before },
    { caption: "After", side: metric.after },
  ];
  return (
    <div className="vc-split">
      <div className="vc-split__legend">
        {[split.counted, split.rest].map((label, i) => (
          <span key={label} className="vc-split__key">
            <span className={`vc-split__dot vc-split__dot--${i === 0 ? "counted" : "rest"}`} />
            {label}
          </span>
        ))}
      </div>
      {sides.map(({ caption, side }) => {
        const parts = splitParts(side, split);
        return (
          <div key={caption} className="vc-split__row">
            <span className="vc-split__caption">{caption}</span>
            {parts === null ? (
              <span className="vc-split__empty">No answers yet</span>
            ) : (
              <>
                {/* One image, not two bars and two numbers: a screen reader gets
                    the whole sentence rather than a pile of unlabelled digits. */}
                <div
                  className="vc-split__bar"
                  role="img"
                  aria-label={`${caption}: ${parts
                    .map((p) => `${p.count} ${p.label.toLowerCase()}`)
                    .join(", ")}`}
                >
                  {parts.map((part, i) => (
                    <span
                      key={part.label}
                      className={`vc-split__seg vc-split__seg--${i === 0 ? "counted" : "rest"}`}
                      style={{ flexGrow: part.count }}
                      title={`${part.label}: ${part.count}`}
                    />
                  ))}
                </div>
                {/* The numbers wear the text token and take their identity from
                    the swatch beside them, in the legend's order. A number
                    painted in its series colour reads as a status. */}
                <span className="vc-split__tally">
                  {parts.map((part, i) => (
                    <span key={part.label} className="vc-split__count">
                      <span
                        className={`vc-split__dot vc-split__dot--${i === 0 ? "counted" : "rest"}`}
                      />
                      {part.count.toLocaleString("en-US")}
                    </span>
                  ))}
                </span>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** One side of the comparison: the figure, the dates it covers, and what it rests on. */
function SideFigure({
  side,
  unit,
  caption,
}: {
  side: CohortSide;
  unit: CohortMetric["unit"];
  caption: string;
}) {
  return (
    <div className="vc-side">
      <span className="vc-side__caption">{caption}</span>
      <span className="vc-side__value">{formatMetric(side.mean, unit)}</span>
      {/* Only the three metrics with a real per-observation distribution carry a
          median (the two rates are 0/1 per observation, and SCOUT cost per search
          is a ratio of two independent aggregates), so a null here means "this
          metric has no middle", not "we failed to compute one" — hence nothing is
          drawn at all rather than a dash. The backend's is an interpolated
          histogram median, accurate to a bin width; "~" says so without making the
          reader go and find that out. */}
      {side.median === null ? null : (
        <span className="vc-side__median">
          <span className="vc-side__median-key">median</span>~{formatMetric(side.median, unit)}
        </span>
      )}
      {/* The range prints even at ZERO observations. It used to collapse to a bare
          "no data", which hid WHICH span was looked at, and that is exactly the
          question an empty column raises.
          `formatDateTime`, not `formatDate`: the cut is hour-precise and anchored
          on the deploy INSTANT, so a date alone would hide where it actually fell
          and re-create the ambiguity the hourly buckets exist to remove. */}
      <span className="vc-side__meta">
        {side.count.toLocaleString("en-US")} obs, {formatDateTime(side.start)} to{" "}
        {formatDateTime(side.end)}
      </span>
    </div>
  );
}

/** The one line a card wears while a cohort filter is active and the card could
 *  not honour it. Said ON the card, in words, because a platform-wide number
 *  sitting quietly among cohort numbers is the misread the filter invites. */
function ScopeNote({ metric, filtered }: { metric: CohortMetric; filtered: boolean }) {
  if (!filtered || metric.scoped !== false) return null;
  return (
    <p className="vc-card__scope" title="This metric has no project to narrow by.">
      Platform-wide: this card ignores the device and country filter.
    </p>
  );
}

function MetricCard({
  metric,
  boundaryDay,
  filtered,
}: {
  metric: CohortMetric;
  boundaryDay: string | null;
  filtered: boolean;
}) {
  // `improved` is the backend's reading of the delta against this metric's own
  // direction, so the colour never has to know that falling cost is good news and
  // falling conversion is not. Null (unknowable, or dead flat) stays neutral.
  const tone =
    metric.improved === null ? "vc-card--flat" : metric.improved ? "vc-card--good" : "vc-card--bad";
  const note = evidenceNote(metric.before.count, metric.after.count);
  return (
    <article className={`vc-card ${tone}`}>
      <header className="vc-card__head">
        <h2 className="vc-card__title">{metric.label}</h2>
        <p className="vc-card__blurb">{metric.blurb}</p>
      </header>
      <ScopeNote metric={metric} filtered={filtered} />

      <div className="vc-card__figures">
        <SideFigure side={metric.before} unit={metric.unit} caption="Before" />
        <div className="vc-delta">
          <span className="vc-delta__value">{formatDeltaPct(metric.deltaPct)}</span>
          <span className="vc-delta__hint">
            {metric.better === "down" ? "lower is better" : "higher is better"}
          </span>
        </div>
        <SideFigure side={metric.after} unit={metric.unit} caption="After" />
      </div>

      {/* Between the figure and the trend: the share, then what it is made OF,
          then how it moved. */}
      <SplitChart metric={metric} />
      <SparkLine metric={metric} boundaryDay={boundaryDay} />
      {note ? <p className="vc-card__note">{note}</p> : null}
    </article>
  );
}

/** The colour one breakdown line wears, everywhere it appears (legend swatch,
 *  table row, chart line): the console's categorical ramp by position, and the
 *  muted text colour for the folded tail, which is not a series but a remainder. */
function lineColour(line: BreakdownLine, index: number): string {
  if (line.key === BREAKDOWN_OTHER) return "var(--rc-color-text-muted)";
  return `var(--rc-chart-${(index % SERIES_COLOURS) + 1})`;
}

/** One line per dimension value over the lookback, on ONE scale, with the
 *  boundary marked — the breakdown's answer to the sparkline. Drawn together
 *  rather than as small multiples because the question is relative ("did India
 *  overtake the US after the release"), which only a shared y axis can show. */
function BreakdownChart({
  lines,
  boundaryDay,
}: {
  lines: BreakdownLine[];
  boundaryDay: string | null;
}) {
  const polylines = breakdownPolylines(lines);
  if (polylines.length === 0) return null;
  const inner = SPARK_H - SPARK_PAD * 2;
  const marker = breakdownBoundaryX(breakdownDays(lines), boundaryDay);
  return (
    <svg
      className="vc-spark vc-lines"
      viewBox={`0 0 ${SPARK_W} ${SPARK_H}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={`Daily projects started per ${lines.length > 1 ? "value" : "line"} over the last 90 days`}
    >
      {marker === null ? null : (
        <line
          className="vc-spark__boundary"
          x1={marker * SPARK_W}
          x2={marker * SPARK_W}
          y1={0}
          y2={SPARK_H}
        />
      )}
      {polylines.map((poly) => {
        const index = lines.findIndex((line) => line.key === poly.key);
        const line = lines[index];
        if (!line || poly.points.length === 0) return null;
        const d = poly.points
          .map(
            (p, i) =>
              `${i === 0 ? "M" : "L"}${(p.x * SPARK_W).toFixed(1)} ${(SPARK_PAD + p.y * inner).toFixed(1)}`,
          )
          .join(" ");
        // A one-point series is a dot, or it is invisible.
        return poly.points.length === 1 ? (
          <circle
            key={poly.key}
            className="vc-lines__dot"
            cx={poly.points[0]!.x * SPARK_W}
            cy={SPARK_PAD + poly.points[0]!.y * inner}
            r={2}
            style={{ fill: lineColour(line, index) }}
          />
        ) : (
          <path
            key={poly.key}
            className="vc-lines__path"
            d={d}
            style={{ stroke: lineColour(line, index) }}
          />
        );
      })}
    </svg>
  );
}

/** A MIX card: who the clients are, either side of the deploy.
 *
 *  Not a before/after figure with a delta, because a mix that shifted is neither
 *  an improvement nor a regression: it is a different set of people arriving, and
 *  the reader's question is "who, and by how much". So the card is a table (one
 *  row per value, count and share on each side, the share change in POINTS) over a
 *  chart of one line per value on a shared scale. The head of the list is shown
 *  and the tail folded into one "Other" row rather than dropped, so the shares
 *  still add up to the whole side. The totals sit in the header, with the
 *  evidence note every card carries. */
function BreakdownCard({
  metric,
  boundaryDay,
  filtered,
}: {
  metric: CohortMetric;
  boundaryDay: string | null;
  filtered: boolean;
}) {
  const lines = breakdownLines(metric);
  const note = evidenceNote(metric.before.count, metric.after.count);
  return (
    <article className="vc-card vc-card--flat vc-card--wide">
      <header className="vc-card__head">
        <h2 className="vc-card__title">{metric.label}</h2>
        <p className="vc-card__blurb">{metric.blurb}</p>
      </header>
      <ScopeNote metric={metric} filtered={filtered} />

      <div className="vc-card__figures">
        <div className="vc-side">
          <span className="vc-side__caption">Before</span>
          <span className="vc-side__value">{metric.before.count.toLocaleString("en-US")}</span>
          <span className="vc-side__meta">
            projects, {formatDateTime(metric.before.start)} to {formatDateTime(metric.before.end)}
          </span>
        </div>
        <div className="vc-delta">
          <span className="vc-delta__hint">a mix, not a score</span>
        </div>
        <div className="vc-side">
          <span className="vc-side__caption">After</span>
          <span className="vc-side__value">{metric.after.count.toLocaleString("en-US")}</span>
          <span className="vc-side__meta">
            projects, {formatDateTime(metric.after.start)} to {formatDateTime(metric.after.end)}
          </span>
        </div>
      </div>

      {lines.length === 0 ? (
        <p className="vc-breakdown__empty">No projects in the lookback yet.</p>
      ) : (
        <>
          <table className="vc-breakdown">
            <TableExport name="cohort-breakdown" label="the cohort breakdown" />
            <thead>
              <tr>
                <th scope="col" className="vc-breakdown__head">
                  {metric.dimension === "device" ? "Device" : "Country"}
                </th>
                <th scope="col" className="vc-breakdown__head">
                  Before
                </th>
                <th scope="col" className="vc-breakdown__head">
                  After
                </th>
                <th scope="col" className="vc-breakdown__head">
                  Change
                </th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line, index) => (
                <tr key={line.key}>
                  <th scope="row" className="vc-breakdown__name">
                    <span
                      className="vc-breakdown__swatch"
                      style={{ background: lineColour(line, index) } as CSSProperties}
                    />
                    {line.label}
                  </th>
                  <td className="vc-breakdown__num">
                    {line.before.count.toLocaleString("en-US")}
                    <span className="vc-breakdown__share">{formatShare(line.before.share)}</span>
                  </td>
                  <td className="vc-breakdown__num">
                    {line.after.count.toLocaleString("en-US")}
                    <span className="vc-breakdown__share">{formatShare(line.after.share)}</span>
                  </td>
                  <td className="vc-breakdown__num">{formatShareDelta(line.deltaShare)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <BreakdownChart lines={lines} boundaryDay={boundaryDay} />
        </>
      )}
      {note ? <p className="vc-card__note">{note}</p> : null}
    </article>
  );
}

/** The whole-population read and the on-demand cohort SLICE, behind one shape.
 *
 *  The scheduled snapshot is read straight from cache; a device or country
 *  selection is a cohort nobody could precompute, so it is built when asked for
 *  and this polls until it is ready (`versionCohortsSliceQuery` owns the bound).
 *  Whichever of the two reads is live runs (the other is disabled, so no request
 *  is made for it), and the page renders ONE payload type either way. */
function useCohortView(
  window: CohortWindow,
  release: string | undefined,
  devices: readonly string[],
  countries: readonly string[],
) {
  const filtered = devices.length > 0 || countries.length > 0;
  const whole = useQuery({ ...versionCohortsQuery(window, release), enabled: !filtered });
  const sliced = useQuery({
    ...versionCohortsSliceQuery(window, release, devices, countries),
    enabled: filtered,
  });
  const active = filtered ? sliced : whole;
  const data = filtered ? (sliced.data?.cohorts ?? null) : (whole.data ?? null);
  // How many times we have asked and been told `computing`, read off the query
  // STATE rather than counted in React state, because the poll owns the cadence
  // (the Funnel range's device, for the same reason).
  const client = useQueryClient();
  const polls =
    client.getQueryState(
      adminKeys.versionCohortsSlice(
        window,
        release ?? "",
        `${[...devices].sort().join(",") || "-"}:${[...countries].sort().join(",") || "-"}`,
      ),
    )?.dataUpdateCount ?? 0;
  return {
    data,
    filtered,
    isPending: active.isPending,
    isError: active.isError,
    error: active.error,
    refetch: () => void active.refetch(),
    dimmed: active.isPlaceholderData,
    computing: filtered && !data && !active.isError,
    gaveUp: filtered && funnelRangeGaveUp(sliced.data?.status, polls) && !sliced.data?.cohorts,
  };
}

export function VersionCohortsPage({
  window,
  onWindowChange,
  release,
  onReleaseChange,
  section,
  onSectionChange,
  devices,
  onDevicesChange,
  countries,
  onCountriesChange,
}: {
  window: CohortWindow;
  onWindowChange: (next: CohortWindow) => void;
  release: string | undefined;
  onReleaseChange: (next: string | undefined) => void;
  section: CohortSection | undefined;
  onSectionChange: (next: CohortSection) => void;
  /** The device classes the cohort is narrowed to. EMPTY = every device. */
  devices: readonly string[];
  onDevicesChange: (next: readonly string[]) => void;
  /** The origin countries the cohort is narrowed to. EMPTY = every country. */
  countries: readonly string[];
  onCountriesChange: (next: readonly string[]) => void;
}) {
  const view = useCohortView(window, release, devices, countries);
  const data = view.data;
  const boundaryDay = boundaryDayOf(data?.boundary?.firstSeenAt);

  // ONE subject at a time — the whole point of the sections split (nineteen
  // cards on one page stopped being readable). Metrics group by their
  // backend-declared `section` in the lib's display order; a section this
  // bundle does not know files under the trailing "More" tab, so a newer
  // backend's card moves tabs rather than vanishing. The active tab lives in
  // the URL beside the other controls; an unknown or newly-empty selection
  // falls back to the first subject that has cards instead of an empty grid.
  const metricList: readonly CohortMetric[] = data?.metrics ?? [];
  const grouped = new Map<CohortSection, CohortMetric[]>();
  for (const metric of metricList) {
    const key = sectionOf(metric);
    const bucket = grouped.get(key);
    if (bucket) bucket.push(metric);
    else grouped.set(key, [metric]);
  }
  const sections = ([...COHORT_SECTIONS, "other"] as readonly CohortSection[]).filter((key) =>
    grouped.has(key),
  );
  const activeSection = section !== undefined && grouped.has(section) ? section : sections[0];
  const visibleMetrics = activeSection === undefined ? [] : (grouped.get(activeSection) ?? []);

  return (
    <div className="ad-page">
      {/* The A/B page shares this area's grant and asks the same question across
          a different boundary, so the two are one tab strip. Without it that
          page would be reachable only by typing its URL: the sidebar lists
          AREAS, and these two are one. */}
      <CohortTabs active="release" />
      <header className="ad-page__head">
        <h1 className="ad-page__title">Version Cohorts</h1>
        <p className="ad-page__sub">
          Pick a deploy and read the same metrics either side of the moment it started serving. The
          cut is the deploy's own timestamp, not its date: the before side is the full window ending
          right at it, and the after side starts at one hour and grows to match. Check the
          observation count under each figure before reading much into a fresh release.
        </p>
      </header>

      <div className="vc-controls">
        <label className="vc-control">
          <span className="vc-control__label">Deploy</span>
          <select
            className="vc-select"
            value={data?.boundary?.release ?? ""}
            disabled={!data || data.deploys.length === 0}
            onChange={(e) => onReleaseChange(e.target.value || undefined)}
          >
            {data && data.deploys.length > 0 ? (
              data.deploys.map((mark) => (
                <option key={mark.release} value={mark.release}>
                  {deployLabel(mark)}
                </option>
              ))
            ) : (
              <option value="">No deploys recorded yet</option>
            )}
          </select>
        </label>

        <div className="vc-windows" role="group" aria-label="Comparison window">
          {COHORT_WINDOWS.map((w) => (
            <button
              key={w}
              type="button"
              className={`vc-win${w === window ? " vc-win--active" : ""}`}
              aria-pressed={w === window}
              onClick={() => onWindowChange(w)}
            >
              {COHORT_WINDOW_LABELS[w]}
            </button>
          ))}
        </div>

        {/* The cohort: what the projects were CREATED on and from. Creation
            stamps, like the Funnel's — not where the client reads Mira today.
            Selecting either moves the page onto the on-demand slice lane. */}
        <DeviceFilter devices={devices} onChange={onDevicesChange} />
        <CountryFilter countries={countries} onChange={onCountriesChange} />

        {/* These numbers are measured by the worker every few minutes, not on this
            request (backend/recruiter/version_cohort_snapshot.py). Say when, next
            to the controls: a cached figure whose age is invisible is one nobody
            can act on, and the reader would otherwise have no way to tell a
            stalled refresh from a flat fortnight. A slice says when IT was
            measured, which is when somebody last asked for that cohort. */}
        {data ? (
          <span className="vc-stamp" title={formatDateTime(data.generatedAt)}>
            Updated {formatClock(data.generatedAt)}
          </span>
        ) : null}
      </div>

      {view.isPending && !view.computing ? <LoadingState label="Rolling up the cohorts…" /> : null}
      {view.isError ? (
        <AreaBoundary area="version_cohorts" error={view.error} onRetry={view.refetch} />
      ) : null}
      {/* A cohort is measured when it is asked for, which takes a few moments.
          Say that plainly rather than showing an empty page: the reader is
          waiting on real work, not on a slow page. */}
      {view.computing && !view.gaveUp ? (
        <LoadingState label="Measuring this cohort. It is counted fresh, so give it a moment…" />
      ) : null}
      {view.gaveUp ? (
        <EmptyState
          title="Still counting"
          message="This cohort is taking longer than expected. Try again, or clear the device and country filters."
          action={{ label: "Try again", onClick: view.refetch }}
        />
      ) : null}

      {data && !data.boundary ? (
        <EmptyState
          title="No deploy recorded in this environment yet"
          message="A release records itself the first time it serves traffic. The next deploy here will
            appear in the dropdown, and the comparison starts from that moment."
        />
      ) : null}

      {data && data.boundary ? (
        <>
          <p className="vc-boundary">
            Comparing across <strong>{shortRelease(data.boundary.release)}</strong>, first served{" "}
            <time dateTime={data.boundary.firstSeenAt ?? undefined}>
              {formatDateTime(data.boundary.firstSeenAt)}
            </time>
            {view.filtered ? (
              <>
                , narrowed to the projects created on the selected devices and from the selected
                countries.
              </>
            ) : (
              "."
            )}
          </p>
          {/* The subject tabs. Rendered from what the payload actually holds,
              so an empty subject never offers a tab to an empty grid, and each
              chip counts its cards so the reader knows what a switch buys. */}
          <div className="vc-sections" role="group" aria-label="Metric subject">
            {sections.map((key) => (
              <button
                key={key}
                type="button"
                className={`vc-section${key === activeSection ? " vc-section--active" : ""}`}
                aria-pressed={key === activeSection}
                onClick={() => onSectionChange(key)}
              >
                {COHORT_SECTION_LABELS[key]}
                <span className="vc-section__count">{grouped.get(key)?.length ?? 0}</span>
              </button>
            ))}
          </div>
          <div className={`vc-grid${view.dimmed ? " vc-grid--dim" : ""}`}>
            {visibleMetrics.map((metric) =>
              metric.kind === "breakdown" ? (
                <BreakdownCard
                  key={metric.key}
                  metric={metric}
                  boundaryDay={boundaryDay}
                  filtered={view.filtered}
                />
              ) : (
                <MetricCard
                  key={metric.key}
                  metric={metric}
                  boundaryDay={boundaryDay}
                  filtered={view.filtered}
                />
              ),
            )}
          </div>
          {/* The one methodological caveat a reader has to hold to use this page
              honestly. It is on screen rather than in a doc because the mistake it
              prevents (reading a slow item that STARTED before the deploy as
              evidence about the deploy) is made while looking at the numbers. */}
          <p className="vc-footnote">
            Durations count in the hour they finished, so work that started before the deploy and
            finished after it lands on the "after" side. The rates are the exception: each counts in
            the hour of its starting moment (the contact made, the finalists revealed, the card
            shown), with the newest hours held back until each one's window is up. The cut itself is
            hour-precise, so the hour containing the deploy sits wholly on the "after" side.
          </p>
        </>
      ) : null}
    </div>
  );
}
